import { Text } from '@zeiss/beyond-online-react';
import { ErrorState } from '@zeiss/lib-components';
import { logError } from '@zeiss/lib-helpers';
import { TFunction } from 'i18next';
import { useEffect } from 'react';
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from 'react-router-dom';

/**
 * RootBoundary
 *
 * @todo
 * - Layout
 */

interface RootBoundaryProps {
  footer: React.ReactNode;
  /**
   * Avoids exposing the real product title and removes call to action button.
   */
  generic?: boolean;
  productTitle: string;
  t: TFunction;
}

export const RootBoundary = ({
  footer,
  generic = false,
  productTitle,
  t,
}: RootBoundaryProps) => {
  const error = useRouteError();
  const navigate = useNavigate();

  const goHome = () => navigate('/');

  useEffect(() => {
    if (isRouteErrorResponse(error)) return;

    logError(error, {
      customErrorType: 'React Router RootBoundary error',
    });
  }, [error]);

  const transactionIdDetails = {
    copiedText: t('action.copied', 'Copied!'),
    copyText: t('action.copy', 'Copy'),
    transactionIdText: t(
      'text.transactionalIdInfo',
      'If the problem persists, contact support and share this error ID:',
    ),
  };

  const PageNotFound = () => (
    <ErrorState
      buttonText={t('pageNotFound.linkText', 'Back to home')}
      description={t(
        'pageNotFound.text',
        'The page you’re looking for does not exist.',
      )}
      footer={footer}
      fullPageLayout
      headline={t('pageNotFound.title', '404 - Page not found')}
      illustrationConfig={{
        img: '404PageNotFound',
      }}
      onBackToHomeClick={goHome}
      onClick={goHome}
      productTitle={generic ? 'ZEISS' : productTitle}
      showCta={generic ? false : true}
      transactionIdDetails={transactionIdDetails}
    />
  );

  const Forbidden = () => (
    <ErrorState
      buttonText={t('forbidden.linkText', 'Back to home')}
      description={t('forbidden.text', 'Access to this page is restricted.')}
      footer={footer}
      fullPageLayout
      headline={t('forbidden.title', '403 - Forbidden')}
      illustrationConfig={{
        img: '403ForbiddenError',
      }}
      onBackToHomeClick={goHome}
      onClick={goHome}
      productTitle={generic ? 'ZEISS' : productTitle}
      showCta={generic ? false : true}
      transactionIdDetails={transactionIdDetails}
    />
  );

  const ServerError = ({ errorCode }: { errorCode: string }) => (
    <ErrorState
      buttonText={t('serverError.linkText', 'Try again')}
      description={t(
        'serverError.text',
        'Something went wrong on our end. Please try again in a few moments.',
      )}
      footer={footer}
      fullPageLayout
      headline={t('serverError.titleX', `${errorCode} - Server error`, {
        x: errorCode,
      })}
      illustrationConfig={{
        img: '500InternalServerError',
      }}
      onBackToHomeClick={goHome}
      onClick={goHome}
      productTitle={generic ? 'ZEISS' : productTitle}
      showCta={generic ? false : true}
      transactionIdDetails={transactionIdDetails}
    />
  );

  // Handle errors thrown from React Router
  /* istanbul ignore else -- @preserve */
  if (isRouteErrorResponse(error)) {
    /* istanbul ignore else -- @preserve */
    if (error.status === 404) {
      return <PageNotFound />;
    }

    /* istanbul ignore if: we don't use actions/loaders -- @preserve */
    if (error.status === 401) {
      return <Text>You aren’t authorized to see this</Text>;
    }

    /* istanbul ignore if: we don't use actions/loaders -- @preserve */
    if (error.status === 503) {
      return <ServerError errorCode={'503'} />;
    }

    /* istanbul ignore if: we don't use actions/loaders -- @preserve */
    if (error.status === 418) {
      return <Text>🫖</Text>;
    }
  }

  // Handle errors thrown from components
  if (error instanceof Response) {
    if (error.status === 403) {
      return <Forbidden />;
    }

    if (error.status === 404) {
      return <PageNotFound />;
    }
  }

  return <Text>Something went wrong</Text>;
};
